import { addDays, format, parseISO } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DateInput } from '@/components/ui'
import { cn } from '@/lib/cn'
import { localToday } from './useDashboardDate'

const stepButton =
  'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/50'

/** Previous week · calendar · next week, plus a way back to live when viewing the past. */
export function DashboardDatePicker({
  date,
  onChange,
}: {
  date: string | null
  onChange: (next: string | null) => void
}): React.ReactElement {
  const today = localToday()
  const current = date ?? today
  const shift = (days: number): string => format(addDays(parseISO(current), days), 'yyyy-MM-dd')
  const isLive = date === null

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => onChange(shift(-7))}
        className={stepButton}
        aria-label="Previous week"
        title="Previous week"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
      </button>
      <div className="w-40">
        <DateInput
          value={current}
          max={today}
          onChange={(event) => onChange(event.target.value || null)}
          aria-label="Dashboard date"
          className="h-9 rounded-lg px-3 text-xs font-medium shadow-none"
        />
      </div>
      <button
        type="button"
        onClick={() => onChange(shift(7))}
        disabled={isLive}
        className={stepButton}
        aria-label="Next week"
        title="Next week"
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => onChange(null)}
        disabled={isLive}
        className={cn(
          'inline-flex h-9 items-center rounded-lg px-3 text-xs font-semibold transition-colors',
          isLive
            ? 'hidden'
            : 'bg-primary text-white hover:bg-primary/90 dark:bg-sky-500 dark:text-gray-950 dark:hover:bg-sky-400'
        )}
      >
        Today
      </button>
    </div>
  )
}
